import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh queue
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Normalize standardized envelope {success, data, message, pagination}
// - If envelope has pagination, transform to {items, total, page, limit, pages}
// - If envelope has only data, unwrap `data` to root of response.data
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'success')) {
      if (body.pagination) {
        const arr = Array.isArray(body.data) ? body.data : [];
        response.data = {
          items: arr,
          data: arr,
          pagination: body.pagination,
          total: body.pagination.total ?? 0,
          page: body.pagination.page ?? 1,
          limit: body.pagination.limit ?? 10,
          pages: body.pagination.pages ?? 1,
          _raw: body,
        };
      } else {
        // If data is null/undefined, keep empty object; else unwrap
        response.data = body.data !== undefined && body.data !== null ? body.data : {};
        // Keep message accessible
        if (body.message) response.data._message = body.message;
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }
      try {
        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken });
        // Handle envelope
        const payload = data?.success !== undefined ? data.data : data;
        localStorage.setItem('access_token', payload.access_token);
        localStorage.setItem('refresh_token', payload.refresh_token);
        api.defaults.headers.common.Authorization = `Bearer ${payload.access_token}`;
        processQueue(null, payload.access_token);
        originalRequest.headers.Authorization = `Bearer ${payload.access_token}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
